import type { TrackingFn } from '@change-corgi/core-react/tracking';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { openUrlWithHiddenAnchor } from '#src/app/shared/share/utils';

import { trackShareCancel, trackShareFailure, trackShareSuccess } from './track';

/**
 * Configuration options for Facebook sharing functionality.
 *
 * @remarks
 * The tracking events follow a specific order:
 * 1. `intent` - Fires when the share process starts, regardless of method (SDK or URL)
 * 2. `legacy_success` - Fires after `intent` to maintain compatibility with existing dashboards/metrics
 * 3. `success` - Behavior varies by method:
 *    - SDK: Fires when share completes successfully
 *    - URL: Fires when share dialog opens
 * 4. `failure` or `cancel` - Fires based on the share outcome
 *
 * @property trackingData - Key-value pairs for tracking metadata
 * @property trackingEvents - Event names for different stages of the share flow
 * @property trackingEvents.intent - Event fired when share process begins
 * @property trackingEvents.legacy_success - Event fired after intent (for dashboard compatibility)
 * @property trackingEvents.success - Event fired on successful share (timing varies by method)
 * @property trackingEvents.failure - Event fired when share fails
 * @property trackingEvents.cancel - Event fired when share is cancelled
 * @property mode - Optional configuration for share method by platform (defaults to SDK if not specified)
 * @property mode.mobile - Share method to use on mobile devices ('sdk' or 'url')
 * @property mode.desktop - Share method to use on desktop devices ('sdk' or 'url')
 * @property urlSharer - Which endpoint the 'url' method opens (see below)
 */
export type Options = {
	trackingData: Record<string, string | number | boolean | undefined>;
	trackingEvents: {
		intent: string;
		legacy_success: string;
		success: string;
		failure: string;
		cancel: string;
	};
	mode?: { mobile: 'sdk' | 'url'; desktop: 'sdk' | 'url' };
	/**
	 * Which endpoint the `url` share method opens. Defaults to the app_id-gated `dialog/feed`
	 * (mobile) / `dialog/share` (desktop) dialogs. Pass `'sharer'` to open Facebook's `sharer.php`
	 * "Create post" composer instead (same URL on both devices) — it doesn't require the FB app
	 * config and avoids the "not logged in" / "This content isn't available right now" errors those
	 * dialogs throw. Opt-in; only Start Strong step 4 sets it.
	 */
	urlSharer?: 'sharer';
};

type ShareWithParams = {
	url: string;
	options: Options;
	utilityContext: UtilityContext;
	track: TrackingFn;
	isDesktop: boolean;
};

const DESKTOP_SHARE_URL = 'https://www.facebook.com/dialog/share';
const MOBILE_SHARE_URL = 'https://m.facebook.com/dialog/feed';
const SHARER_SHARE_URL = 'https://www.facebook.com/sharer/sharer.php';

/** Builds the Facebook share endpoint URL for the 'url' method (sharer.php or the app_id dialogs). */
function buildFacebookShareUrl(
	url: string,
	appId: string,
	isDesktop: boolean,
	urlSharer: Options['urlSharer'],
): string {
	if (urlSharer === 'sharer') {
		// sharer.php opens Facebook's "Create post" composer with the link attached. Unlike the
		// app_id-gated dialog/feed & dialog/share endpoints it needs no FB app config and avoids
		// their "not logged in" / "This content isn't available right now" error pages.
		const shareUrl = new URL(SHARER_SHARE_URL);
		shareUrl.searchParams.append('u', url);
		return shareUrl.toString();
	}
	const shareUrl = isDesktop ? new URL(DESKTOP_SHARE_URL) : new URL(MOBILE_SHARE_URL);
	shareUrl.searchParams.append('app_id', appId);
	shareUrl.searchParams.append('href', url);
	if (!isDesktop) {
		shareUrl.searchParams.append('link', url);
	}
	return shareUrl.toString();
}

export async function shareWithUrl({
	url,
	options,
	utilityContext,
	track,
	isDesktop,
}: ShareWithParams): Promise<boolean> {
	const { trackingEvents, trackingData, urlSharer } = options;
	const { errorReporter, facebook } = utilityContext;
	try {
		const shareUrlStr = buildFacebookShareUrl(url, facebook.appId, isDesktop, urlSharer);
		if (urlSharer === 'sharer' && !isDesktop) {
			// On mobile the sharer.php link hands off to the Facebook app. Opening it with
			// window.open('_blank') spawns a new tab that's left blank once the OS switches to the app;
			// navigate the current tab via a hidden anchor instead so no orphaned tab is left behind.
			openUrlWithHiddenAnchor(shareUrlStr);
		} else {
			await Promise.resolve(window.open(shareUrlStr, '_blank'));
		}

		trackShareSuccess(trackingEvents.success, trackingData, utilityContext, track);

		return true;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (e: any) {
		void errorReporter.report(new Error(`Could not share to facebook: ${e}`));

		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
		trackShareFailure(trackingEvents.failure, { error: e.toString() }, trackingData, utilityContext, track);
		return false;
	}
}

export async function shareWithSdk({
	url,
	options,
	utilityContext,
	track,
	isDesktop,
}: ShareWithParams): Promise<boolean> {
	const { trackingEvents, trackingData } = options;
	const { errorReporter, facebook } = utilityContext;

	try {
		let params: fb.ShareOpenGraphDialogParams | fb.ShareDialogParams;

		if (isDesktop) {
			params = {
				// the SDK defaults to iframe on desktop when the user
				// is logged in with their FB account
				display: 'popup',
				action_type: 'og.likes',
				action_properties: {
					object: url,
				},
				href: url,
				method: 'share_open_graph',
			};
		} else {
			/**
			 * On mobile, the `share_open_graph` method does not work properly,
			 * so we should keep using the `share` method until we can confirm
			 * migrating is safe.
			 */
			params = {
				href: url,
				method: 'share',
			};
		}

		const response = await facebook.ui(params);

		if (response.cancelled) {
			trackShareCancel(trackingEvents.cancel, trackingData, utilityContext, track);
			return false;
		}

		trackShareSuccess(trackingEvents.success, trackingData, utilityContext, track);

		return true;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (e: any) {
		void errorReporter.report(new Error(`Could not share to facebook: ${e}`));

		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
		trackShareFailure(trackingEvents.failure, { error: e.toString() }, trackingData, utilityContext, track);
		return false;
	}
}
